// Implements: F-UIX-019
/**
 * The two reads of the platform totals view, through the shared query cache.
 *
 * The totals are keyed by their range, so an answer for a range the admin has since left
 * is cached under its own key and never shown for the new one (checklist 14); while the
 * new range loads, the previous one stays on screen rather than a blank view. The names
 * are one list for the whole view: a failed or slow name read never hides the totals,
 * which then name each organization by its id.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { OrganizationListView, PlatformTotalsView } from "../../../types/reports";
import { fetchOrganizations, fetchPlatformTotals } from "../api/platformApi";
import type { DayRange } from "../api/platformApi";

export function platformTotalsKey(
  range: DayRange,
): readonly ["admin", "platformTotals", string, string] {
  return ["admin", "platformTotals", range.from, range.to];
}

export const ORGANIZATION_NAMES_KEY = ["admin", "organizations"] as const;

export function usePlatformTotals(range: DayRange): UseQueryResult<PlatformTotalsView, ApiError> {
  return useQuery<PlatformTotalsView, ApiError>({
    queryKey: platformTotalsKey(range),
    queryFn: ({ signal }) => fetchPlatformTotals(range, signal),
    // The previous range stays on screen while the next one loads, instead of a blank view.
    placeholderData: keepPreviousData,
  });
}

export function useOrganizationList(): UseQueryResult<OrganizationListView, ApiError> {
  return useQuery<OrganizationListView, ApiError>({
    queryKey: ORGANIZATION_NAMES_KEY,
    queryFn: ({ signal }) => fetchOrganizations(signal),
  });
}
